import type { MenuProps } from "antd";

export const PROFILE = {
  VISITANTE: "Visitante",
  OPERADOR: "Operador",
  GESTOR: "Gestor",
  ADMINISTRADOR: "Administrador",
} as const;

export type MenuItem = NonNullable<Required<MenuProps>["items"]>[number];

export function profileKey(s?: string | null): string {
  return String(s ?? "").trim().toLowerCase();
}

export function isAdministradorLabel(label?: string | null): boolean {
  return profileKey(label) === profileKey(PROFILE.ADMINISTRADOR);
}

export function canGestorActOnTargetUserProfile(targetDescricao?: string | null): boolean {
  const k = profileKey(targetDescricao);
  if (!k || isAdministradorLabel(targetDescricao)) return false;
  return (
    k === profileKey(PROFILE.VISITANTE) ||
    k === profileKey(PROFILE.OPERADOR) ||
    k === profileKey(PROFILE.GESTOR)
  );
}

export function decodeJwtPayload(token: string): Record<string, unknown> | null {
  try {
    const part = token.split(".")[1];
    if (!part) return null;
    const b64 = part.replace(/-/g, "+").replace(/_/g, "/");
    const padded = b64 + "===".slice((b64.length + 3) % 4);
    const json = decodeURIComponent(
      Array.prototype.map
        .call(atob(padded), (c: string) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
        .join("")
    );
    return JSON.parse(json);
  } catch {
    return null;
  }
}

/** Primeiro segmento da rota (ex.: historic, acessos). Raiz conta como pesagem. */
export function primaryRouteSegment(pathname: string): string {
  const segs = pathname.replace(/^\//, "").split("/").filter(Boolean);
  if (segs.length === 0) return "weighingRail";
  return segs[0];
}

export function routeAllowedForProfile(
  pathname: string,
  profileRaw?: string | null
): boolean {
  const first = primaryRouteSegment(pathname);
  const p = profileKey(profileRaw);

  if (p === profileKey(PROFILE.ADMINISTRADOR)) return true;

  if (p === profileKey(PROFILE.VISITANTE)) {
    return first === "weighingRail" || first === "logoff";
  }

  if (p === profileKey(PROFILE.OPERADOR)) {
    return first === "weighingRail" || first === "historic" || first === "logoff";
  }

  if (p === profileKey(PROFILE.GESTOR)) {
    if (first === "logoff") return true;
    if (first === "weighingRail" || first === "historic" || first === "acessos") {
      return true;
    }
    if (first === "carregamento") return true;
    if (first === "detalhesCarregamento") return true;
    return false;
  }

  return first === "weighingRail" || first === "logoff";
}

export function filterNavigationByProfile(
  items: MenuItem[],
  profileRaw?: string | null
): MenuItem[] {
  const p = profileKey(profileRaw);
  if (p === profileKey(PROFILE.ADMINISTRADOR)) {
    return items;
  }

  const allowTop = new Set<string>();
  const allowChild = new Set<string>();

  if (p === profileKey(PROFILE.VISITANTE)) {
    allowTop.add("weighingRail");
    allowTop.add("sair");
  } else if (p === profileKey(PROFILE.OPERADOR)) {
    allowTop.add("weighingRail");
    allowTop.add("historic");
    allowTop.add("sair");
  } else if (p === profileKey(PROFILE.GESTOR)) {
    allowTop.add("weighingRail");
    allowTop.add("historic");
    allowTop.add("configuracoes");
    allowTop.add("sair");
    allowChild.add("acessos");
  } else {
    allowTop.add("weighingRail");
    allowTop.add("sair");
  }

  return (items || [])
    .map((item) => {
      if (!item || typeof item === "string") return item;
      const key = String((item as { key?: string }).key ?? "");
      if (!allowTop.has(key)) return null;

      if ("children" in item && Array.isArray((item as { children?: MenuItem[] }).children)) {
        const rawChildren = (item as { children: MenuItem[] }).children;
        const children = rawChildren
          .map((c) => {
            if (!c || typeof c === "string") return null;
            const ck = String((c as { key?: string }).key ?? "");
            if (allowChild.size > 0 && !allowChild.has(ck)) return null;
            return c;
          })
          .filter(Boolean) as MenuItem[];
        return { ...item, children: children.length ? children : undefined } as MenuItem;
      }
      return item;
    })
    .filter(Boolean) as MenuItem[];
}
